import { Close as CloseIcon } from '@mui/icons-material';
import { Button, Grid, IconButton, ImageList } from '@mui/material';
import { Controller } from 'react-hook-form';
import { InformationBox } from 'src/components/form/informationBox/InformationBox';
import { SearchBar } from 'src/components/input';
import { ColorPickerWidget } from 'src/components/widget';
import {
  checkAssetMatch,
  filterAssetsBySearchName,
} from 'src/components/widget/resourceSelectorWidget/ResourceSelector.helper';
import { useResourceSelector } from 'src/components/widget/resourceSelectorWidget/ResourceSelector.hook';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { ResourceSelectorSidebarHeader } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/resourceSelectorSidebarHeader';
import { ResourceSelectorSidebarItem } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/resourceSelectorSidebarItem';
import { sortAssetsByUploadDate } from 'src/hooks/api/assets/helper';
import { IAsset } from 'src/interfaces';

import { BackgroundFormControllerProps } from './BackgroundController.interface';

export const BackgroundFormController = ({
  onToggleBackgroundDrawer,
  customer,
  control,
  onChange: handleOnChange,
  componentDetails,
  assets,
  loading: isLoadingAssets,
  isMop,
  refetchAssets,
}: BackgroundFormControllerProps) => {
  const { searchVal, selectedAssets, onChangeSearchVal, onSelectedAssets } =
    useResourceSelector();

  const sortedAssets = sortAssetsByUploadDate(assets);

  const filteredAssets = filterAssetsBySearchName(
    sortedAssets,
    searchVal,
    ResourceType.BACKGROUND,
  );

  return (
    <Grid
      container
      gap={1}
      padding={2}
      direction="column"
      wrap="nowrap"
      sx={{ overflowY: 'auto' }}>
      <ResourceSelectorSidebarHeader
        label="Background"
        onBackClick={onToggleBackgroundDrawer}
        showUploadButton={true}
        refetchAssets={refetchAssets}
      />
      <InformationBox>
        Optimal background size for {componentDetails?.name} is{' '}
        {componentDetails?.width} * {componentDetails?.height} px
      </InformationBox>

      {!isMop && (
        <Controller
          key={`${customer}.backgroundColor`}
          name={`${customer}.backgroundColor`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Grid item container gap={1} alignItems="center">
              <ColorPickerWidget
                {...field}
                value={field.value}
                ref={ref}
                options={{
                  showHEX: true,
                  hideAIcon: true,
                  backgroundColor: true,
                  border: true,
                }}
                onChange={(value) =>
                  handleOnChange(
                    `${customer}.backgroundColor`,
                    value,
                    field.onChange,
                  )
                }
              />
              {field.value && (
                <IconButton
                  size="small"
                  onClick={() => {
                    handleOnChange(
                      `${customer}.backgroundColor`,
                      '',
                      field.onChange,
                    );
                  }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Grid>
          )}
        />
      )}

      <SearchBar
        value={searchVal}
        isLoading={isLoadingAssets}
        onInputChange={onChangeSearchVal}
      />

      <Controller
        key={`${customer}${isMop ? '.mop' : ''}.background`}
        name={`${customer}${isMop ? '.mop' : ''}.background`}
        control={control}
        render={({ field }) => {
          const defaultAsset = assets.find(
            (item) => item.assetName === field.value,
          );

          return (
            <>
              <ImageList rowHeight={120} gap={6} cols={3}>
                {filteredAssets.map((asset: IAsset) => {
                  const isSelected = checkAssetMatch(
                    selectedAssets,
                    asset,
                    defaultAsset,
                  );

                  return (
                    <ResourceSelectorSidebarItem
                      type={ResourceType.BACKGROUND}
                      key={asset.assetId}
                      fileName={asset.fileName}
                      src={asset.thumbnailUrl}
                      isSelected={isSelected}
                      backgroundColor={
                        componentDetails?.config?.[customer]?.backgroundColor
                      }
                      onClick={() => {
                        onSelectedAssets([asset]);
                        handleOnChange(
                          `${customer}${isMop ? '.mop' : ''}.background`,
                          asset.fileName,
                          field.onChange,
                        );
                      }}
                    />
                  );
                })}
              </ImageList>
              <div>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    onSelectedAssets([]);
                    handleOnChange(
                      `${customer}${isMop ? '.mop' : ''}.background`,
                      '',
                      field.onChange,
                    );
                  }}>
                  Clear
                </Button>
              </div>
            </>
          );
        }}
      />
    </Grid>
  );
};
